import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SxProps, Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useSortableList } from './useSortableList';
import { dropAnimationConfig } from './dropAnimationConfig';
import { screenReaderInstructions } from './screenReaderInstructions';

export function SortableList<Item extends { id: string }>({
  initialItems,
  getLabel,
  onUpdateItems,
  DragOverlayRenderer,
  children,
}: {
  initialItems: Item[];
  getLabel: (item: Item) => string;
  onUpdateItems: (items: Item[]) => void;
  DragOverlayRenderer: (props: {
    item: Item;
    sx?: SxProps<Theme>;
    index: number;
  }) => React.ReactNode;
  children?: React.ReactNode;
}) {
  const [items, setItems] = useState(initialItems);

  const {
    announcements,
    setActiveId,
    activeIndex,
    activeId,
    getIndex,
    sensors,
  } = useSortableList({ items });

  useEffect(() => {
    // Reset the items when the initial items change.
    setItems(initialItems);
  }, [initialItems]);

  return (
    <DndContext
      sensors={sensors}
      accessibility={{
        announcements: announcements,
        screenReaderInstructions,
      }}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }

        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          const overIndex = getIndex(over.id);

          if (activeIndex !== overIndex) {
            const newOrder = arrayMove(items, activeIndex, overIndex);
            setItems(newOrder);
            onUpdateItems(newOrder);
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
      modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
      <DragOverlay dropAnimation={dropAnimationConfig} wrapperElement={'ul'}>
        {activeId ? (
          <DragOverlayRenderer
            sx={{
              backgroundColor: (theme) => theme.palette.grey[500],
              boxShadow: (theme) => theme.shadows[4],
              transform: 'scale(1.1)',
            }}
            item={items[activeIndex]}
            index={activeIndex}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
